/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  Paper,
  Divider,
  Chip,
  Stack,
  Fade,
  useMediaQuery,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from "@mui/material";
import {
  SportsEsports,
  Groups,
  Event,
  TrendingUp,
  LocationOn,
  AccessTime,
  Instagram,
  YouTube,
  Twitter
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import FeaturedSection from "../components/ui/FeaturedSection";
import CalendarSection from "../components/ui/CalendarSection";
import MKBox from "../components/MKBox";
import MKButton from "../components/MKButton";
import MKTypography from "../components/MKTypography";
import {
  PRIMARY_PINK,
  TRANSPARENT_WHITE_10,
  TRANSPARENT_WHITE_90,
  TRANSPARENT_BLACK_30,
  TRANSPARENT_BLACK_50,
  SHADOW_BLACK_30,
  SHADOW_BLACK_15,
  SHADOW_BLACK_10,
} from "../constants";

function HomePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"), { noSsr: true });
  const isMobileOrSmall = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const [showContent, setShowContent] = React.useState(false);
  const [homeTab, setHomeTab] = React.useState(0);

  // Social media links
  const socialLinks = [
    { 
      name: 'YouTube', 
      url: 'https://www.youtube.com/@sandiegogamingyt', 
      icon: <YouTube />, 
      color: '#FF0000' 
    },
    { 
      name: 'Instagram', 
      url: 'https://www.instagram.com/sandiegogaming_official/', 
      icon: <Instagram />, 
      color: '#E4405F' 
    },
    { 
      name: 'Discord', 
      url: 'https://discord.com/invite/bzGbNjMfXf', 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
      ), 
      color: '#5865F2' 
    },
    { 
      name: 'Twitch', 
      url: 'https://www.twitch.tv/sandiegogaming_official', 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
        </svg>
      ), 
      color: '#9146FF' 
    },
    { 
      name: 'Twitter/X', 
      url: 'https://x.com/sandiegogaming', 
      icon: <Twitter />, 
      color: '#1DA1F2' 
    }
  ];

  // Show content after video finishes (1 minute 13 seconds = 73 seconds)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 8000); // 1 minute 13 seconds

    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: <SportsEsports sx={{ fontSize: 40 }} />,
      title: "Gaming Communities",
      description: "Join specialized gaming clubs and connect with like-minded players in San Diego.",
      color: "#2196f3"
    },
    {
      icon: <Event sx={{ fontSize: 40 }} />,
      title: "Events & Tournaments",
      description: "Discover local gaming events, tournaments, and community meetups.",
      color: "#f50057"
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: "Community Building",
      description: "Create and manage your own gaming community with powerful tools.",
      color: "#4caf50"
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Stay Connected",
      description: "Get updates on events, news, and community activities in real-time.",
      color: "#ff9800"
    }
  ];

  const stats = [
    { label: "Active Clubs", value: "25+" },
    { label: "Events This Month", value: "50+" },
    { label: "Community Members", value: "1000+" }
  ];

  return (
    <Box sx={{ 
      minHeight: "100vh",
      overflowX: "hidden",
      width: "100%"
    }}>
      {/* Links Section */}
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f8f9fa',
          borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#404040' : '#e0e0e0'}`
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2, sm: 3 }
            }}
          >
            <MKTypography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: PRIMARY_PINK,
                fontSize: { xs: '1rem', sm: '1.125rem' },
                mb: { xs: 1, sm: 0 }
              }}
            >
              Follow Us:
            </MKTypography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: { xs: 1, sm: 1.5 }
              }}
            >
              {socialLinks.map((link, index) => (
                <Tooltip key={index} title={link.name}>
                  <IconButton
                    onClick={() => window.open(link.url, '_blank')}
                    sx={{
                      color: theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary',
                      '&:hover': {
                        color: link.color,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {link.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, sm: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: '60vh', sm: '50vh', md: '50vh' }
        }}
      >
        {/* Video Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            opacity: showContent ? 0 : 1,
            transition: 'opacity 1.5s ease-in-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <video
            autoPlay
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            aria-label="San Diego Gaming promotional video"
          >
            <source src="/sd-gaming-animated.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>

        {/* Content Overlay */}
        <Fade in={showContent} timeout={1500}>
          <Container 
            maxWidth="lg" 
            sx={{ 
              position: 'relative', 
              zIndex: 2,
              opacity: showContent ? 1 : 0,
              transition: 'opacity 1.5s ease-in-out'
            }}
          >
            {/* Default San Diego Gaming Content */}
            <Grid container spacing={{ xs: 2, sm: 4 }} alignItems="center">
                <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
                  <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                    <MKTypography
                      component="h1"
                      variant="h2"
                      fontWeight="bold"
                      mb={{ xs: 2, sm: 3 }}

                      sx={{
                        fontSize: (isMobile || isMobileOrSmall) ? '1.5rem' : { sm: '2.5rem', md: '3.5rem' },
                        lineHeight: 1.2,
                        color: '#ffffff',
                        textShadow: `2px 2px 8px ${SHADOW_BLACK_15}`






                      }}

                    >
                      San Diego Gaming
                    </MKTypography>
                    <MKTypography
                      variant="h5"
                      mb={{ xs: 3, sm: 4 }}

                      sx={{

                        color: '#ffffff',
                        fontWeight: 400,
                        lineHeight: 1.6,
                        textShadow: `1px 1px 4px ${SHADOW_BLACK_30}`,
                        fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                        px: { xs: 1, sm: 0 }




                      }}

                    >
                      Connect, compete, and create with San Diego's premier gaming community platform.
                    </MKTypography>
                    <Stack 
                      direction={{ xs: 'column', sm: 'row' }} 
                      spacing={2}
                      sx={{ 
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        maxWidth: { xs: '300px', sm: 'none' },
                        mx: { xs: 'auto', md: 0 }
                      }}
                    >
                      <MKButton
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/clubs')}
                        sx={{
                          bgcolor: '#ffffff',
                          color: '#333333',
                          fontWeight: 600,
                          px: { xs: 3, sm: 4 },
                          py: 1.5,
                          width: { xs: '100%', sm: 'auto' },
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '&:hover': {
                            bgcolor: '#f5f5f5'
                          }
                        }}
                        aria-label="Navigate to clubs page to explore gaming clubs"
                      >
                        Explore Clubs
                      </MKButton>
                      <MKButton
                        variant="outlined"
                        size="large"
                        onClick={() => navigate('/create-club')}
                        sx={{
                          borderColor: '#ffffff',
                          color: '#ffffff',
                          fontWeight: 600,
                          px: { xs: 3, sm: 4 },
                          py: 1.5,
                          width: { xs: '100%', sm: 'auto' },
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '&:hover': {
                            borderColor: '#ffffff',
                            bgcolor: TRANSPARENT_WHITE_10
                          }
                        }}
                        aria-label="Navigate to create club page to start a new gaming club"
                      >
                        Create Club
                      </MKButton>
                    </Stack>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                  <Box
                    sx={{




                      display: 'flex',

                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      mb: { xs: 2, md: 0 }
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 150, sm: 200, md: 300 },
                        height: { xs: 150, sm: 200, md: 300 },
                        borderRadius: '50%',
                        background: TRANSPARENT_WHITE_10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.2)'
                      }}
                    >
                      <img 
                        src="/sd-gaming-logo.png" 
                        alt="San Diego Gaming Logo"
                        style={{
                          width: '162%',
                          height: '162%',
                          objectFit: 'contain',
                          filter: `drop-shadow(2px 2px 4px ${SHADOW_BLACK_30})`
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
            </Grid>
          </Container>
        </Fade>
      </Box>

      {/* Featured Events / Monthly Calendar */}
      <Box
        sx={{
          py: 8,
          background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f8f9fa'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2 }}>
          <Tabs
            value={homeTab}
            onChange={(e, newValue) => setHomeTab(newValue)}
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{
              mb: 1,
              minWidth: { sm: 360 },
              '& .MuiTab-root': {
                fontWeight: 700,
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                textTransform: 'none',
                color: theme.palette.mode === 'dark' ? '#cccccc' : '#555555'
              },
              '& .Mui-selected': {
                color: `${PRIMARY_PINK} !important`
              },
              '& .MuiTabs-indicator': {
                backgroundColor: PRIMARY_PINK,
                height: 3
              }
            }}
          >
            <Tab label="Featured Events" />
            <Tab label="Monthly Calendar" />
          </Tabs>
        </Box>
        {homeTab === 0 ? <FeaturedSection /> : <CalendarSection />}
      </Box>


      {/* Stats Section */}
      {/* <Box
        sx={{
          py: 6,
          background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f8f9fa'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={4} key={index}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 3
                  }}
                >
                  <MKTypography
                    variant="h3"
                    fontWeight="bold"
                    mb={1}
                    sx={{ color: PRIMARY_PINK }}
                  >
                    {stat.value}
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {stat.label}
                  </MKTypography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Features Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <MKTypography
              component="h2"
              variant="h3"
              fontWeight="bold"
              mb={2}
              sx={{ 
                color: '#e91e63',
                fontSize: { xs: '1.75rem', sm: '2.125rem', md: '3rem' }
              }}
            >
              Why Choose San Diego Gaming?
            </MKTypography>
            <MKTypography
              variant="h6"
              color="text.secondary"
              sx={{ 
                maxWidth: 600, 
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                px: { xs: 2, sm: 0 }
              }}
            >
              Everything you need to build and grow your gaming community
            </MKTypography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: { xs: 2, sm: 3 },
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 60, sm: 80 },
                        height: { xs: 60, sm: 80 },
                        borderRadius: '50%',
                        background: `${feature.color}15`,
                        color: feature.color,
                        mb: { xs: 2, sm: 3 }
                      }}
                    >
                      {React.cloneElement(feature.icon, { 
                        sx: { fontSize: { xs: 30, sm: 40 } } 
                      })}
                    </Box>
                    <MKTypography
                      variant="h6"
                      fontWeight="bold"
                      mb={2}
                      sx={{ 
                        color: PRIMARY_PINK,
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }}
                    >
                      {feature.title}
                    </MKTypography>
                    <MKTypography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.875rem', sm: '0.875rem' }
                      }}
                    >
                      {feature.description}
                    </MKTypography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Paper
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3
            }}
          >
            <MKTypography
              component="h2"
              variant="h4"
              fontWeight="bold"
              mb={3}
              sx={{ 
                color: 'white !important',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
              }}
            >
              Ready to Join the Community?
            </MKTypography>
            <MKTypography
              variant="h6"
              mb={4}
              sx={{ 
                opacity: 0.9, 
                color: 'white !important',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                px: { xs: 2, sm: 0 }
              }}
            >
              Start your gaming journey today and connect with players across San Diego
            </MKTypography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <MKButton
                variant="contained"
                size="large"
                onClick={() => navigate('/clubs')}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.mode === 'dark' ? '#1a1a1a' : '#667eea',
                  fontWeight: 600,
                  px: { xs: 3, sm: 4 },
                  py: 1.5,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    bgcolor: TRANSPARENT_WHITE_90
                  }
                }}
              >
                Browse Clubs
              </MKButton>
              <MKButton
                variant="outlined"
                size="large"
                onClick={() => navigate('/create-club')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 600,
                  px: { xs: 3, sm: 4 },
                  py: 1.5,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.8)',
                    bgcolor: TRANSPARENT_WHITE_10
                  }
                }}
              >
                Create Your Club
              </MKButton>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}

export default HomePage;
